/**
 * Unify Coverage model.
 *
 * Represents a patient's insurance coverage. Created during intake and
 * linked to the patient. Used by the Sohar VOB integration for eligibility
 * verification and benefits checking.
 *
 * Canonical field mapping (FHIR R4 — note: R4 Coverage has no `kind`):
 *
 * ┌──────────────────────┬───────────────────────────────────────────────────────────┐
 * │ Field                │ Description                                               │
 * ├──────────────────────┼───────────────────────────────────────────────────────────┤
 * │ status               │ "active" | "cancelled" | "entered-in-error"               │
 * │ beneficiary          │ Reference<Patient> — the covered patient                  │
 * │ subscriber           │ Reference<Patient | RelatedPerson> — the policy holder    │
 * │ relationship         │ CodeableConcept (subscriber-relationship): self/spouse/…  │
 * │ subscriberId         │ Member ID (insurer-assigned subscriber id)                │
 * │ identifier[MB]       │ Member ID — type.coding "MB" + type.text "member-id"      │
 * │ payor[]              │ Reference<Organization>[] (+ display) — insurance payer   │
 * │ class[group]         │ Employer group number — type.coding "group", value        │
 * │ class[plan]          │ Plan details — type.text/coding "plan", value, name       │
 * │ period               │ { start, end } — coverage effective dates                 │
 * └──────────────────────┴───────────────────────────────────────────────────────────┘
 *
 * Extension summary:
 *   COVERAGE_INSURANCE_TYPE_EXT — staff insurance type (commercial | medicare | medicaid)
 *
 * Access control:
 *   Coverage is compartment-scoped via the patient's CareTeam.
 */
import type { Coverage, Patient, Reference, RelatedPerson } from '@medplum/fhirtypes';
import {
  COVERAGE_CLASS_SYSTEM,
  COVERAGE_MEMBER_ID_CODE,
  IDENTIFIER_TYPE_SYSTEM,
  SUBSCRIBER_RELATIONSHIP_SYSTEM,
} from '../constants';

// ═══════════════════════════════════════════════════════════════════════
//  Literal union types
// ═══════════════════════════════════════════════════════════════════════

/** Coverage statuses used by Unify. */
export type CoverageStatus = 'active' | 'cancelled' | 'entered-in-error';

/** @deprecated R4 Coverage has no `kind` field; retained for backwards compatibility only. */
export type CoverageKind = 'insurance';

/** Subscriber-to-beneficiary relationship codes used by Unify intake. */
export type CoverageRelationship = 'self' | 'spouse' | 'child' | 'other';

/** FHIR Coverage.order slot (1 = primary, 2 = secondary, 3 = tertiary). */
export type CoverageSlotOrder = 1 | 2 | 3;

/** Portal / UI label for a coverage slot. */
export type CoveragePriorityLabel = 'primary' | 'secondary' | 'tertiary';

/** Maps FHIR Coverage.order (1-based) to a priority label. */
export function coverageOrderToPriority(order?: number): CoveragePriorityLabel {
  if (order === 2) return 'secondary';
  if (order === 3) return 'tertiary';
  return 'primary';
}

/** Maps a priority label to FHIR Coverage.order. */
export function priorityToCoverageOrder(priority: CoveragePriorityLabel): CoverageSlotOrder {
  if (priority === 'secondary') return 2;
  if (priority === 'tertiary') return 3;
  return 1;
}

/** True when Coverage is the primary slot (`order === 1` only). */
export function isPrimaryCoverage(coverage: Coverage): boolean {
  return coverage.order === 1;
}

/** Calendar `YYYY-MM-DD` prefix of a FHIR date/dateTime, or undefined. */
export function fhirDateOnly(value?: string): string | undefined {
  const day = value?.slice(0, 10);
  return day && /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : undefined;
}

/**
 * True when `serviceDate` (YYYY-MM-DD) falls in Coverage.period.
 * Missing start or end is open-ended on that side.
 */
export function coveragePeriodContainsDate(
  coverage: Pick<Coverage, 'period'>,
  serviceDate: string,
): boolean {
  const date = fhirDateOnly(serviceDate);
  if (!date) return false;
  const start = fhirDateOnly(coverage.period?.start);
  const end = fhirDateOnly(coverage.period?.end);
  return (!start || start <= date) && (!end || date <= end);
}

/** Active primary Coverage whose period contains `serviceDate`. */
export function isEligiblePrimaryCoverage(
  coverage: Coverage,
  serviceDate: string,
): boolean {
  return coverage.status === 'active' && isPrimaryCoverage(coverage) && coveragePeriodContainsDate(coverage, serviceDate);
}

/**
 * Returns the patient's active primary Coverage (`order === 1`) for VOB/billing.
 * When multiple primaries exist, picks deterministically by Coverage id.
 */
export function getPrimaryActiveCoverage(coverages: Coverage[]): Coverage | undefined {
  const primaries = coverages.filter((c) => c.status === 'active' && c.order === 1);
  if (primaries.length === 0) return undefined;
  return [...primaries].sort((a, b) => (a.id ?? '').localeCompare(b.id ?? ''))[0];
}

// ═══════════════════════════════════════════════════════════════════════
//  Getter helpers
// ═══════════════════════════════════════════════════════════════════════

/** Returns the beneficiary (Patient) reference. */
export function getCoverageBeneficiaryRef(resource: Coverage): Reference | undefined {
  return resource.beneficiary;
}

/** Returns the first payor (Organization) reference. */
export function getCoveragePayorRef(resource: Coverage): Reference | undefined {
  return resource.payor?.[0];
}

/** Returns the subscriber (Patient | RelatedPerson) reference if present. */
export function getCoverageSubscriberRef(resource: Coverage): Reference | undefined {
  return resource.subscriber;
}

/** Returns the plan class value (e.g. "PILOT-DEMO"). */
export function getCoveragePlanValue(resource: Coverage): string | undefined {
  return resource.class?.find((c) => c.type?.text === 'plan')?.value;
}

/** Returns the plan class name. */
export function getCoveragePlanName(resource: Coverage): string | undefined {
  return resource.class?.find((c) => c.type?.text === 'plan')?.name;
}

/**
 * Returns the member id — `subscriberId`, or the `MB` / "member-id" identifier
 * as a fallback. Matches the conventions read by the VOB and billing readers.
 */
export function getCoverageMemberId(resource: Coverage): string | undefined {
  return (
    resource.identifier?.find(
      (i) =>
        i.type?.coding?.some((c) => c.code === COVERAGE_MEMBER_ID_CODE) ||
        i.type?.text === 'member-id',
    )?.value ?? resource.subscriberId
  );
}

/** Returns the employer group number from `class[group]`. */
export function getCoverageGroupValue(resource: Coverage): string | undefined {
  return resource.class?.find(
    (c) => c.type?.coding?.some((cc) => cc.code === 'group') || c.type?.text === 'group',
  )?.value;
}

/** Returns the subscriber-to-beneficiary relationship code (self/spouse/child/other). */
export function getCoverageRelationship(resource: Coverage): string | undefined {
  return resource.relationship?.coding?.find((c) => c.system === SUBSCRIBER_RELATIONSHIP_SYSTEM)
    ?.code;
}

// ═══════════════════════════════════════════════════════════════════════
//  Builder
// ═══════════════════════════════════════════════════════════════════════

export interface BuildIntakeCoverageParams {
  /** Beneficiary (covered patient) reference, e.g. "Patient/{id}". */
  patientRef: string;
  patientDisplay?: string;
  /** Existing payer Organization id — becomes `payor[0].reference`. */
  payerOrgId: string;
  /** Payer display name (insurance company) — becomes `payor[0].display`. */
  payerName: string;
  /** Insurer-assigned member id. */
  memberId: string;
  /** Employer group number (optional). */
  groupId?: string;
  /** Subscriber relationship to the beneficiary. */
  relationship: CoverageRelationship;
  /**
   * Subscriber reference when the policy holder is NOT the patient
   * (e.g. "RelatedPerson/{id}"). Omitted when `relationship` is "self".
   */
  subscriberRef?: string;
  subscriberDisplay?: string;
  /** Coverage effective dates (YYYY-MM-DD). */
  periodStart?: string;
  periodEnd?: string;
  /** Coordination-of-benefits slot (1 = primary, 2 = secondary, 3 = tertiary). */
  order?: CoverageSlotOrder;
  /** Human-readable plan name (stored on class[plan].name). */
  planName?: string;
  /** Plan code / identifier (stored on class[plan].value; defaults from planName when omitted). */
  planValue?: string;
}

/**
 * Builds a spec-compliant intake `Coverage`. Emits both `subscriberId` and an
 * `MB`/"member-id" identifier so the VOB (`readMemberId`) and billing
 * (`coverageToInsuranceEntry`) readers both resolve the member id; the group
 * number goes on `class[group]`, and the payer is a real `Organization` reference.
 */
export function buildIntakeCoverage(params: BuildIntakeCoverageParams): Coverage {
  const beneficiary: Reference<Patient> = {
    reference: params.patientRef,
    ...(params.patientDisplay ? { display: params.patientDisplay } : {}),
  };

  const subscriber: Reference<Patient | RelatedPerson> =
    params.relationship === 'self' || !params.subscriberRef
      ? beneficiary
      : {
          reference: params.subscriberRef,
          ...(params.subscriberDisplay ? { display: params.subscriberDisplay } : {}),
        };

  const coverage: Coverage = {
    resourceType: 'Coverage',
    status: 'active',
    beneficiary,
    subscriber,
    subscriberId: params.memberId,
    relationship: {
      coding: [{ system: SUBSCRIBER_RELATIONSHIP_SYSTEM, code: params.relationship }],
    },
    identifier: [
      {
        type: {
          coding: [{ system: IDENTIFIER_TYPE_SYSTEM, code: COVERAGE_MEMBER_ID_CODE }],
          text: 'member-id',
        },
        value: params.memberId,
      },
    ],
    payor: [{ reference: `Organization/${params.payerOrgId}`, display: params.payerName }],
    order: params.order ?? 1,
  };

  const classes: NonNullable<Coverage['class']> = [];
  if (params.groupId) {
    classes.push({
      type: { coding: [{ system: COVERAGE_CLASS_SYSTEM, code: 'group' }], text: 'group' },
      value: params.groupId,
    });
  }
  if (params.planName || params.planValue) {
    classes.push({
      type: { coding: [{ system: COVERAGE_CLASS_SYSTEM, code: 'plan' }], text: 'plan' },
      value: params.planValue ?? params.planName ?? 'plan',
      ...(params.planName ? { name: params.planName } : {}),
    });
  }
  if (classes.length > 0) {
    coverage.class = classes;
  }

  if (params.periodStart || params.periodEnd) {
    coverage.period = {
      ...(params.periodStart ? { start: params.periodStart } : {}),
      ...(params.periodEnd ? { end: params.periodEnd } : {}),
    };
  }

  return coverage;
}
